import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, UserPlus, UserMinus, Edit2, ClipboardCheck, CalendarDays, ExternalLink, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_BALANCE = { 'Casual Leave': 12, 'Sick Leave': 12, 'Earned Leave': 15, 'Comp Off': 0, 'Work From Home': 24, 'Leave Without Pay': 0 };

export default function EmployeeDirectory() {
  const [employees, setEmployees] = useState([]);
  const [onboardingForms, setOnboardingForms] = useState([]);
  const [user, setUser] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [profileForm, setProfileForm] = useState({});
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [offboardOpen, setOffboardOpen] = useState(false);
  const [leaveBalanceOpen, setLeaveBalanceOpen] = useState(false);
  const [actionEmp, setActionEmp] = useState(null);
  const [actionForm, setActionForm] = useState({});
  const [balanceForm, setBalanceForm] = useState({});
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  const token = localStorage.getItem('token');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
  const isAdmin = user?.role === 'admin' || user?.role === 'admin_support';
  const isManagement = user && ['admin', 'admin_support', 'team_leader'].includes(user.role);

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API}/hrms/employees`, { headers: getHeaders() });
      setEmployees(res.data);
    } catch (e) { toast.error('Failed to fetch employees'); }
  };

  const fetchForms = async () => {
    try {
      const res = await axios.get(`${API}/hrms/onboarding-form`, { headers: getHeaders() });
      setOnboardingForms(res.data);
    } catch (err) { console.error("./pages/EmployeeDirectory.jsx:", err); }
  };

  useEffect(() => { fetchEmployees(); if (isManagement) fetchForms(); }, [user]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/hrms/employees/${selectedEmp.id}/profile`, profileForm, { headers: getHeaders() });
      toast.success('Profile updated'); setProfileOpen(false); fetchEmployees();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update profile'); }
  };

  const handleOnboard = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/hrms/employees/${actionEmp.id}/onboard`, actionForm, { headers: getHeaders() });
      // If "trigger onboarding form" is checked, create blank form for employee
      if (actionForm.trigger_onboarding_form) {
        await axios.post(`${API}/hrms/onboarding-form/trigger/${actionEmp.id}`, {}, { headers: getHeaders() });
      }
      toast.success('Employee onboarded' + (actionForm.trigger_onboarding_form ? ' — onboarding form assigned' : ''));
      setOnboardOpen(false); fetchEmployees(); fetchForms();
    } catch (e) { toast.error('Failed to onboard'); }
  };

  const handleTriggerForm = async (empId, empName) => {
    try {
      await axios.post(`${API}/hrms/onboarding-form/trigger/${empId}`, {}, { headers: getHeaders() });
      toast.success(`Onboarding form triggered for ${empName}`);
      fetchForms();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to trigger form'); }
  };

  const handleResendForm = async (formId, empName) => {
    const reason = window.prompt(`Reason for resending form to ${empName}:`, 'Please review and resubmit with corrections.');
    if (reason === null) return;
    try {
      await axios.post(`${API}/hrms/onboarding-form/${formId}/resend`, { reason }, { headers: getHeaders() });
      toast.success(`Onboarding form resent to ${empName} for resubmission`);
      fetchForms();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to resend form'); }
  };

  const handleOffboard = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/hrms/employees/${actionEmp.id}/offboard`, actionForm, { headers: getHeaders() });
      toast.success('Employee offboarded'); setOffboardOpen(false); fetchEmployees();
    } catch (e) { toast.error('Failed to offboard'); }
  };

  const openLeaveBalance = async (emp) => {
    setActionEmp(emp);
    try {
      const res = await axios.get(`${API}/hrms/leave-balance/${emp.id}`, { headers: getHeaders() });
      setBalanceForm(res.data.balances || { ...DEFAULT_BALANCE });
    } catch (e) { setBalanceForm({ ...DEFAULT_BALANCE }); }
    setLeaveBalanceOpen(true);
  };

  const handleSaveBalance = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/hrms/leave-balance/${actionEmp.id}`, { balances: balanceForm }, { headers: getHeaders() });
      toast.success('Leave balance updated'); setLeaveBalanceOpen(false);
    } catch (e) { toast.error('Failed to update balance'); }
  };

  const getStatusBadge = (profile) => {
    if (!profile?.employment_status) return <span className="bg-slate-100 text-slate-600 px-2 py-1 text-xs font-bold rounded">New</span>;
    if (profile.employment_status === 'active') return <span className="bg-emerald-100 text-emerald-800 px-2 py-1 text-xs font-bold rounded">Active</span>;
    return <span className="bg-red-100 text-red-800 px-2 py-1 text-xs font-bold rounded">Exited</span>;
  };

  const hasOnboardingForm = (empId) => onboardingForms.some(f => f.user_id === empId);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="employees-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Employee Directory</h1>
        <p className="text-slate-600">Manage employee profiles, onboarding & offboarding</p>
      </div>

      <div className="bg-white border border-blue-100 rounded shadow-sm overflow-x-auto">
        <div className="p-3 border-b border-blue-100 bg-blue-50/40">
          <div className="relative max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="employees-search-input"
              placeholder="Search by name, email, emp #, designation, department, team…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-9 rounded border-blue-200"
            />
          </div>
        </div>
        <Table className="min-w-[1200px]">
          <TableHeader>
            <TableRow className="bg-blue-50 border-b border-blue-100">
              <TableHead className="font-bold text-blue-900">Emp #</TableHead>
              <TableHead className="font-bold text-blue-900">Name</TableHead>
              <TableHead className="font-bold text-blue-900">Email</TableHead>
              {isManagement && <TableHead className="font-bold text-blue-900">Role</TableHead>}
              {!isManagement && <TableHead className="font-bold text-blue-900">Mobile No.</TableHead>}
              <TableHead className="font-bold text-blue-900">Designation</TableHead>
              <TableHead className="font-bold text-blue-900">Department</TableHead>
              {isManagement && <TableHead className="font-bold text-blue-900">Team</TableHead>}
              {isManagement && <TableHead className="font-bold text-blue-900">Status</TableHead>}
              {isManagement && <TableHead className="font-bold text-blue-900">Phone</TableHead>}
              <TableHead className="font-bold text-blue-900">DOJ</TableHead>
              {isManagement && <TableHead className="font-bold text-blue-900">DOB</TableHead>}
              {isManagement && <TableHead className="font-bold text-blue-900">Onboarding</TableHead>}
              {isManagement && <TableHead className="font-bold text-blue-900">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {(() => {
              const q = searchText.trim().toLowerCase();
              const filteredEmps = q
                ? employees.filter(emp =>
                    (emp.name || '').toLowerCase().includes(q) ||
                    (emp.email || '').toLowerCase().includes(q) ||
                    (emp.employee_number || '').toLowerCase().includes(q) ||
                    (emp.designation || '').toLowerCase().includes(q) ||
                    (emp.department || '').toLowerCase().includes(q) ||
                    (emp.team_name || '').toLowerCase().includes(q) ||
                    (emp.role || '').toLowerCase().includes(q)
                  )
                : employees;
              return filteredEmps.length === 0 ? (
              <TableRow><TableCell colSpan={isManagement ? 13 : 7} className="text-center text-slate-500 py-8"><Users size={48} className="mx-auto mb-2 text-slate-300" />{q ? 'No employees match your search.' : 'No employees'}</TableCell></TableRow>
            ) : filteredEmps.map(emp => (
              <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`} className="hover:bg-blue-50">
                <TableCell className="text-sm font-mono text-slate-700" data-testid={`emp-num-${emp.id}`}>{emp.employee_number || <span className="text-slate-400">-</span>}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">{emp.name?.charAt(0)}</div>
                    <Link to={`/profile/${emp.id}`} className="font-semibold text-blue-900 hover:text-blue-600 hover:underline">{emp.name}</Link>
                  </div>
                </TableCell>
                <TableCell className="text-sm">{emp.email}</TableCell>
                {isManagement && <TableCell><span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-0.5 rounded uppercase">{emp.role?.replace('_', ' ')}</span></TableCell>}
                {!isManagement && <TableCell className="text-sm font-medium">{emp.profile?.phone || <span className="text-slate-400">-</span>}</TableCell>}
                <TableCell className="text-sm text-slate-600" data-testid={`emp-designation-${emp.id}`}>{emp.designation || emp.profile?.designation || <span className="text-slate-400">-</span>}</TableCell>
                <TableCell className="text-sm text-slate-600" data-testid={`emp-department-${emp.id}`}>{emp.department || <span className="text-slate-400">-</span>}</TableCell>
                {isManagement && <TableCell className="text-sm" data-testid={`emp-team-${emp.id}`}>{emp.team_name || <span className="text-slate-400">-</span>}</TableCell>}
                {isManagement && <TableCell>{getStatusBadge(emp.profile)}</TableCell>}
                {isManagement && <TableCell className="text-sm">{emp.profile?.phone || '-'}</TableCell>}
                <TableCell className="text-sm text-slate-600" data-testid={`emp-doj-${emp.id}`}>{emp.date_of_joining ? new Date(emp.date_of_joining).toLocaleDateString() : emp.profile?.joining_date || <span className="text-slate-400">-</span>}</TableCell>
                {isManagement && <TableCell className="text-sm text-slate-600" data-testid={`emp-dob-${emp.id}`}>{emp.date_of_birth ? new Date(emp.date_of_birth).toLocaleDateString() : <span className="text-slate-400">-</span>}</TableCell>}
                {isManagement && (
                <TableCell>
                  {(() => {
                    const form = onboardingForms.find(f => f.user_id === emp.id);
                    if (form && ['submitted', 'approved'].includes(form.status)) {
                      return (
                        <div className="flex items-center gap-1.5">
                          <span className="bg-emerald-100 text-emerald-800 text-xs font-bold px-2 py-0.5 rounded">{form.status === 'approved' ? 'Approved' : 'Submitted'}</span>
                          {isAdmin && (
                            <Button size="sm" variant="ghost" data-testid={`resend-form-${emp.id}`} onClick={() => handleResendForm(form.id, emp.name)} className="text-amber-600 hover:text-amber-900 hover:bg-amber-50 text-xs h-6 px-2" title="Resend form for corrections">
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mr-0.5"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/><path d="M21 21v-5h-5"/></svg>
                              Resend
                            </Button>
                          )}
                        </div>
                      );
                    } else if (form && form.status === 'resent') {
                      return <span className="bg-amber-100 text-amber-800 text-xs font-bold px-2 py-0.5 rounded">Resent - Awaiting</span>;
                    } else if (form) {
                      return <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-0.5 rounded">{form.status || 'Pending'}</span>;
                    } else {
                      return isAdmin ? (
                        <Button size="sm" variant="outline" data-testid={`trigger-form-${emp.id}`} onClick={() => handleTriggerForm(emp.id, emp.name)} className="text-blue-600 border-blue-200 text-xs h-7"><ClipboardCheck size={12} className="mr-1" />Send Form</Button>
                      ) : <span className="text-xs text-slate-400">Pending</span>;
                    }
                  })()}
                </TableCell>
                )}
                {isManagement && (
                <TableCell>
                  <div className="flex gap-1">
                    {isManagement && (
                      <Button size="sm" variant="ghost" data-testid={`edit-profile-${emp.id}`} onClick={() => { setSelectedEmp(emp); setProfileForm(emp.profile || {}); setProfileOpen(true); }} className="text-blue-600 hover:text-blue-900 hover:bg-blue-50" title="Edit Profile"><Edit2 size={14} /></Button>
                    )}
                    {isAdmin && (
                      <Button size="sm" variant="ghost" data-testid={`manage-leaves-${emp.id}`} onClick={() => openLeaveBalance(emp)} className="text-amber-600 hover:text-amber-900 hover:bg-amber-50" title="Manage Leave Balance"><CalendarDays size={14} /></Button>
                    )}
                    {isAdmin && (!emp.profile?.employment_status || emp.profile?.employment_status !== 'active') && (
                      <Button size="sm" variant="ghost" data-testid={`onboard-${emp.id}`} onClick={() => { setActionEmp(emp); setActionForm({ joining_date: new Date().toISOString().split('T')[0], notes: '', trigger_onboarding_form: true }); setOnboardOpen(true); }} className="text-emerald-600 hover:text-emerald-900 hover:bg-emerald-50" title="Onboard"><UserPlus size={14} /></Button>
                    )}
                    {isAdmin && emp.profile?.employment_status === 'active' && (
                      <Button size="sm" variant="ghost" data-testid={`offboard-${emp.id}`} onClick={() => { setActionEmp(emp); setActionForm({ exit_date: new Date().toISOString().split('T')[0], reason: '', notes: '' }); setOffboardOpen(true); }} className="text-red-600 hover:text-red-800 hover:bg-red-50" title="Offboard"><UserMinus size={14} /></Button>
                    )}
                  </div>
                </TableCell>
                )}
              </TableRow>
            ));
            })()}
          </TableBody>
        </Table>
      </div>

      {/* Profile Dialog */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="rounded max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">Employee Profile — {selectedEmp?.name}</DialogTitle>
            <DialogDescription>View and edit personal information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveProfile} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Phone</Label>
                <Input value={profileForm.phone || ''} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} className="mt-2 rounded border-blue-200" placeholder="Phone number" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Date of Birth</Label>
                <Input type="date" value={profileForm.dob || ''} onChange={(e) => setProfileForm({ ...profileForm, dob: e.target.value })} className="mt-2 rounded border-blue-200" /></div>
            </div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Address</Label>
              <textarea value={profileForm.address || ''} onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })} className="mt-2 flex w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm min-h-[60px]" placeholder="Full address" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Emergency Contact Name</Label>
                <Input value={profileForm.emergency_contact_name || ''} onChange={(e) => setProfileForm({ ...profileForm, emergency_contact_name: e.target.value })} className="mt-2 rounded border-blue-200" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Emergency Contact Phone</Label>
                <Input value={profileForm.emergency_contact_phone || ''} onChange={(e) => setProfileForm({ ...profileForm, emergency_contact_phone: e.target.value })} className="mt-2 rounded border-blue-200" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Blood Group</Label>
                <Input value={profileForm.blood_group || ''} onChange={(e) => setProfileForm({ ...profileForm, blood_group: e.target.value })} className="mt-2 rounded border-blue-200" placeholder="e.g., O+" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Designation</Label>
                <Input value={profileForm.designation || ''} onChange={(e) => setProfileForm({ ...profileForm, designation: e.target.value })} className="mt-2 rounded border-blue-200" /></div>
            </div>
            <Button type="submit" data-testid="save-profile-btn" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">Save Profile</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Onboard Dialog */}
      <Dialog open={onboardOpen} onOpenChange={setOnboardOpen}>
        <DialogContent className="rounded">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">Onboard — {actionEmp?.name}</DialogTitle>
            <DialogDescription>Mark employee as active and optionally trigger onboarding form</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleOnboard} className="space-y-4 mt-4">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Joining Date</Label>
              <Input data-testid="onboard-date" type="date" value={actionForm.joining_date || ''} onChange={(e) => setActionForm({ ...actionForm, joining_date: e.target.value })} required className="mt-2 rounded border-blue-200" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Notes</Label>
              <textarea data-testid="onboard-notes" value={actionForm.notes || ''} onChange={(e) => setActionForm({ ...actionForm, notes: e.target.value })} className="mt-2 flex w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm min-h-[60px]" placeholder="Onboarding notes..." /></div>
            <label className="flex items-center gap-2 cursor-pointer bg-blue-50 border border-blue-200 rounded p-3">
              <input type="checkbox" data-testid="trigger-form-checkbox" checked={actionForm.trigger_onboarding_form || false} onChange={(e) => setActionForm({ ...actionForm, trigger_onboarding_form: e.target.checked })} className="w-4 h-4 rounded border-blue-300" />
              <div>
                <span className="text-sm font-bold text-blue-900">Trigger Onboarding Form</span>
                <p className="text-xs text-slate-500">Employee will see the joining form in their HRMS section to fill out</p>
              </div>
            </label>
            <Button type="submit" data-testid="submit-onboard-btn" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold">Onboard Employee</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Offboard Dialog */}
      <Dialog open={offboardOpen} onOpenChange={setOffboardOpen}>
        <DialogContent className="rounded">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">Offboard — {actionEmp?.name}</DialogTitle>
            <DialogDescription>Process employee exit</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleOffboard} className="space-y-4 mt-4">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Exit Date</Label>
              <Input data-testid="offboard-date" type="date" value={actionForm.exit_date || ''} onChange={(e) => setActionForm({ ...actionForm, exit_date: e.target.value })} required className="mt-2 rounded border-blue-200" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Reason</Label>
              <select data-testid="offboard-reason" value={actionForm.reason || ''} onChange={(e) => setActionForm({ ...actionForm, reason: e.target.value })} className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm">
                <option value="">Select reason</option><option value="Resignation">Resignation</option><option value="Termination">Termination</option><option value="Contract End">Contract End</option><option value="Retirement">Retirement</option><option value="Other">Other</option>
              </select></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Notes</Label>
              <textarea data-testid="offboard-notes" value={actionForm.notes || ''} onChange={(e) => setActionForm({ ...actionForm, notes: e.target.value })} className="mt-2 flex w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm min-h-[60px]" placeholder="Exit notes..." /></div>
            <Button type="submit" data-testid="submit-offboard-btn" className="w-full bg-red-600 hover:bg-red-700 text-white rounded font-bold">Process Offboarding</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Leave Balance Management Dialog */}
      <Dialog open={leaveBalanceOpen} onOpenChange={setLeaveBalanceOpen}>
        <DialogContent className="rounded max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">Leave Balance — {actionEmp?.name}</DialogTitle>
            <DialogDescription>Define, adjust, or carry forward leave balances</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveBalance} className="space-y-3 mt-4">
            <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded">Set exact balance for each leave type. Use this to define initial allocation, carry forward from previous year, or adjust mid-year.</p>
            {Object.keys(DEFAULT_BALANCE).map(type => (
              <div key={type} className="flex items-center gap-3">
                <Label className="text-xs font-bold uppercase tracking-wider text-blue-900 w-40">{type}</Label>
                <Input data-testid={`balance-${type.replace(/\s/g, '-').toLowerCase()}`} type="number" min="0" value={balanceForm[type] ?? 0} onChange={(e) => setBalanceForm({ ...balanceForm, [type]: parseInt(e.target.value) || 0 })} className="rounded border-blue-200 w-24 text-center font-bold" />
                <span className="text-xs text-slate-400">days</span>
              </div>
            ))}
            <div className="bg-amber-50 border border-amber-200 rounded p-3 mt-2">
              <p className="text-xs text-amber-800"><strong>Carry Forward:</strong> Simply increase the balance for the relevant leave type by the number of days to carry forward.</p>
            </div>
            <Button type="submit" data-testid="save-balance-btn" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold mt-2">Save Balance</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
